import { normalizeFusionResultCode } from "./fusionResult";
import { projectNodePaths } from "./projectNodePaths";

export const TREE_METRICS = [
  ["total", "분석 대상 파일 수"], ["image", "이미지 파일 수"], ["video", "동영상 파일 수"],
  ["normal", "정상 수"], ["caution", "요주의 수"], ["abnormal", "이상 수"], ["recheck", "재점검 요망 수"],
  ["electrical", "전기 설비 수"], ["gas", "가스 설비 수"], ["mechanical", "기계 설비 수"],
  ["normalRate", "정상 비율"], ["cautionRate", "요주의 비율"], ["abnormalRate", "이상 비율"],
  ["buildings", "건물 수"], ["floors", "층 수"], ["locations", "위치 수"], ["facilities", "전체 설비 수"],
];
export const TREE_CHARTS = [["resultChart", "진단 결과 분포"], ["fileTypeChart", "파일 유형별 비율"], ["typeChart", "설비 구분별 비율"]];
export const DEFAULT_TREE_METRICS = ["total", "normal", "caution", "abnormal", "resultChart", "fileTypeChart", "typeChart"];
export const DEFAULT_TREE_CHART_STYLES = { resultChart: "donut", fileTypeChart: "donut", typeChart: "bar" };
export const TREE_NODE_COUNT_METRIC_IDS = ["buildings", "floors", "locations"];
const NODE_LEVELS = [["building", "building_name", "treeBuildingName", "buildings"], ["floor", "floor", "treeFloor", "floors"], ["location", "location", "treeLocation", "locations"]];

export function getTreeNodeMetrics(nodes = [], scope = {}) {
  const depth = NODE_LEVELS.reduce((last, [, , key], index) => scope[key] ? index : last, -1);
  const allowed = scope.treeFacilityType || scope.treeFacilityName || scope.treePartName || scope.category || scope.index || scope.value
    ? [] : NODE_LEVELS.slice(depth + 1).map(([, , , id]) => id);
  const types = new Map(nodes.map(node => [node.id, node.node_type]));
  const counts = { buildings: 0, floors: 0, locations: 0 };
  const seen = new Set();
  projectNodePaths(nodes).forEach(path => {
    if (seen.has(path.id) || !NODE_LEVELS.every(([, field, key]) => !scope[key] || (scope[key] === "__unassigned__" ? !path[field] : path[field] === scope[key]))) return;
    seen.add(path.id);
    const metric = NODE_LEVELS.find(([type]) => types.get(path.id) === type)?.[3];
    if (allowed.includes(metric)) counts[metric] += 1;
  });
  return { counts, options: TREE_METRICS.filter(([id]) => !TREE_NODE_COUNT_METRIC_IDS.includes(id) || allowed.includes(id)) };
}

export function readTreeChartStyles(key) {
  try {
    const stored = JSON.parse(localStorage.getItem(`${key}:chart-styles`));
    return Object.fromEntries(TREE_CHARTS.map(([id]) => [id, ["donut", "bar"].includes(stored?.[id]) ? stored[id] : DEFAULT_TREE_CHART_STYLES[id]]));
  } catch { return { ...DEFAULT_TREE_CHART_STYLES }; }
}
export function readTreeMetrics(key) {
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    const valid = new Set([...TREE_METRICS, ...TREE_CHARTS].map(([id]) => id));
    const selected = Array.isArray(stored) ? [...new Set(stored.filter(id => valid.has(id)))] : [];
    return selected.length ? selected : DEFAULT_TREE_METRICS;
  } catch { return DEFAULT_TREE_METRICS; }
}
function equipmentType(value) {
  const type = String(value ?? "").trim().replace(/\s+/g, "").toLowerCase();
  if (["전기", "전기설비", "electrical", "electric"].includes(type)) return "electrical";
  if (["가스", "가스설비", "gas"].includes(type)) return "gas";
  if (["기계", "기계설비", "mechanical", "machine"].includes(type)) return "mechanical";
  return type ? "other" : "unspecified";
}

export function buildTreeMetrics(files = []) {
  const data = { total: files.length, image: 0, video: 0, normal: 0, caution: 0, abnormal: 0, recheck: 0, unknown: 0 };
  const facilities = new Map();
  files.forEach(file => {
    if (file.file_type === "image" || file.file_type === "video") data[file.file_type] += 1;
    const code = normalizeFusionResultCode(file.fusion_result_code ?? file.fusion_result);
    data[["normal", "caution", "abnormal", "recheck"][code] || "unknown"] += 1;
    const name = String(file.facility_name ?? "").trim();
    if (name) {
      const key = JSON.stringify([file.building_name, file.floor, file.location, name].map(value => String(value ?? "").trim()));
      if (!facilities.has(key)) facilities.set(key, new Set());
      facilities.get(key).add(equipmentType(file.facility_type));
    }
  });
  const judged = data.total - data.unknown;
  const types = { electrical: 0, gas: 0, mechanical: 0, other: 0, unspecified: 0 };
  facilities.forEach(values => {
    const known = [...values].filter(value => value !== "unspecified");
    types[known.length > 1 ? "other" : known[0] || "unspecified"] += 1;
  });
  const rate = value => judged ? `${Math.round(value / judged * 100)}%` : "—";
  return { ...data, ...types, normalRate: rate(data.normal), cautionRate: rate(data.caution), abnormalRate: rate(data.abnormal), facilities: facilities.size,
    resultChart: [["정상", data.normal], ["요주의", data.caution], ["이상", data.abnormal], ["재점검 요망", data.recheck], ["판정 미등록", data.unknown]],
    fileTypeChart: [["이미지", data.image], ["동영상", data.video], ["기타", data.total - data.image - data.video]],
    typeChart: [["전기", types.electrical], ["가스", types.gas], ["기계", types.mechanical], ["기타·분류 불일치", types.other], ["미지정", types.unspecified]],
  };
}
